import { useMutation, useQueryClient } from "react-query";
import React from "react";
import * as wf from "@qpr/client-api-workflow";
import { FieldValue, TSubmitValues } from "backend/datainput";
import { clearElementCache } from "@qpr/client-api-base/querybuilder/ElementCache";

const useSubmitElementValues = () => {
  const queryClient = useQueryClient();
  const invalidateCache = React.useMemo(
    () => ({
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.indexOf("data-") >= 0,
        });
      },
    }),
    [queryClient]
  );
  // was: types.MeasurePayload

  return useMutation(async (data: TSubmitValues) => {
    const dataPerWorkflow: Record<string, FieldValue[]> = {};
    const addField = (id: string, value: FieldValue) => {
      if (!dataPerWorkflow[id]) dataPerWorkflow[id] = [];
      dataPerWorkflow[id].push(value);
    };
    data.fields.forEach((f) => {
      if (f.field.element?.workflowInstance?.wfInstanceId) {
        addField(f.field.element?.workflowInstance?.wfInstanceId, f);
      }
    });

    const workflowIdsToSend = Object.keys(dataPerWorkflow);

    // eslint-disable-next-line
    for (const wfId of workflowIdsToSend) {
      const fields = dataPerWorkflow[wfId];
      const [f1] = fields;
      const firstInstance = f1.field.element?.workflowInstance;
      if (!firstInstance) {
        continue;
      }

      /**
       * Collect comment entries (Actions and Recommendsations, Justification etc.)
       */
      const commentEntries = fields.filter(
        (f) => f.field.inputType === "comment"
      );
      const allFiles: File[] = [];
      fields
        .filter((f) => f.field.inputType === "attachments")
        .forEach((item) => {
          item.files?.forEach((file) => {
            allFiles.push(file);
          });
        });

      const clearElements: Record<string, true> = {};
      for (const comment of commentEntries) {
        if (comment.field.comments && comment.field.comments.length) {
          const [originalComment] = comment.field.comments;
          await wf.updateComment({
            commentId: originalComment.id,
            comment: comment.value,
            wfId,
          });
        } else {
          await wf.createComment({
            comment: comment.value,
            parentId: firstInstance.meaId,
            period: firstInstance.period,
          });
        }
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.indexOf("comments-") >= 0,
        });
      }

      /**
       * Collect data for Element Series
       */

      const seriesToSend = [
        ...fields
          .filter((value) => value.series)
          .filter((value) => value.series !== "MESSAGE")
          .map((item) => {
            if (item.field.element?.id) {
              clearElements[item.field.element?.id] = true;
            }
            return {
              value: item.value,
              series: item.series || "",
              period: firstInstance.period || "",
              elementId: item.field.element?.id || "",
            };
          }),
      ];

      if (data.transition === "SUBMIT" || !data.transition) {
        seriesToSend.push({
          value: "0",
          period: firstInstance.period || "",
          series: "STATUS",
          elementId: firstInstance?.meaId || "",
        });
      }

      if (allFiles.length > 0) {
        await wf.createAttachments({
          parentId: firstInstance.meaId,
          period: firstInstance.period,
          files: allFiles,
        });
      }

      if (seriesToSend.length > 0) {
        await wf.postMeasureValues({
          wfId: firstInstance.wfInstanceId,
          values: seriesToSend,
        });
        clearElementCache(Object.keys(clearElements));
      }
      const clearIdList = Object.keys(clearElements);
      if (clearIdList.length > 0) {
        clearElementCache(clearIdList);
      }
      await wf.updateWorkflowInstanceState({
        id: firstInstance.wfInstanceId,
        transition: {
          transition: data.transition || "SUBMIT",
          message: data.message || (data.transition === "SAVE" ? "Draft saved" : "Data submitted") || "",
        },
      });
    }
  }, invalidateCache);
};

export default useSubmitElementValues;
